import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import Button from '../../shared/button.component';
import AgGrid from '../../shared/agGrid/agGrid.component';
import CheckboxCellRenderer from '../../shared/agGrid/cellRenderers/checkboxCellRenderer.component';
import { TaxRateChangePropType } from './propTypes';
import rateChangeWorkpaperColumnDefs from '../../shared/columnDefinitions/rateChangeWorkpaper';
import PromptDialog from '../../shared/promptDialog.component';
import NavigationPrompt from '../../shared/navigationPrompt.component';
import {
  assignmentPermissions,
  assignmentTypes,
  checkAccessBasedOnAssignmentPermissions,
} from '../../shared/authorization';
import { UserWithoutActiveFlagPropTypes } from '../../shared/propTypes/users';
import { editedFlag } from './highlightEdited';

class RateChangeWorkpaperGrid extends PureComponent {
  static propTypes = {
    rateChangeRows: PropTypes.arrayOf(TaxRateChangePropType).isRequired,
    submitRateChanges: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isSubmittingRows: PropTypes.bool.isRequired,
    user: UserWithoutActiveFlagPropTypes.isRequired,
    showAlert: PropTypes.func.isRequired,
  };

  state = {
    rateChangesForReview: [],
    promptBulkNewRateDialog: false,
    updatedRateChanges: {},
  };

  componentDidUpdate(prevProps) {
    const submittedRows = prevProps.isSubmittingRows && !this.props.isSubmittingRows;
    if (submittedRows) {
      this.clearUpdatedRateChanges();
    }
  }

  preventSettingPreviousValue = false;
  previousNewRateValue = '';

  handleCheckboxSelectionChanged = selectedRateChangeId => {
    this.setState(({ rateChangesForReview }) => {
      if (rateChangesForReview.includes(selectedRateChangeId)) {
        return {
          rateChangesForReview: rateChangesForReview.filter(
            rateChangeId => rateChangeId !== selectedRateChangeId
          ),
        };
      }

      return {
        rateChangesForReview: [
          ...rateChangesForReview,
          selectedRateChangeId,
        ],
      };
    }, () => {
      this.refreshCells();
    });
  };

  selectAllRows = () => {
    const filteredRows = this.getFilteredRowNodes();

    this.setState({
      rateChangesForReview: filteredRows.map(row => row.data.rateChangeId),
    }, () => {
      this.refreshCells();
    });
  };

  clearAllRows = () => {
    this.setState({
      rateChangesForReview: [],
    }, () => {
      this.refreshCells();
    });
  };

  isAnyRowSelected = () => Boolean(this.state.rateChangesForReview.length);

  getSelectedRowNodes = () => this.state.rateChangesForReview.map(rowId =>
    this.gridApi.getRowNode(rowId)
  );

  getFilteredRowNodes = () => {
    const rowNodes = [];
    this.gridApi.forEachNodeAfterFilter(rowNode => rowNodes.push(rowNode));
    return rowNodes;
  };

  refreshCells = () => {
    this.gridApi.refreshCells({ force: true });
  };

  submitSelectedCells = () => {
    const { rateChangesForReview } = this.state;
    const rateChangeRows = Object.values(this.state.updatedRateChanges);

    const selectedRows = rateChangeRows.filter(rateChange =>
      rateChangesForReview.includes(rateChange.rateChangeId) && parseFloat(rateChange.rateChangeEntry) !== 0);

    this.props.submitRateChanges(selectedRows);
    this.refreshCells();
  };

  handleTextInputFocus = params => {
    this.previousNewRateValue = params.value;
  };

  handleNewRateBlur = params => {
    if (!this.isAllowedToChange(params.data)) {
      this.resetPreviousNewValue(params);
      return this.props.showAlert('You cannot edit this row because you do not have permission to edit the company code');
    }

    if (this.preventSettingPreviousValue) {
      this.preventSettingPreviousValue = false;
      this.setNewRate(params);
      this.refreshCells();
    } else {
      this.resetPreviousNewValue(params);
    }
  };

  setNewRate({ node, data: rowData }) {
    const newAdjustedNetBalance = parseFloat(rowData.grossBalance) * parseFloat(rowData.newRate);
    const newRateChangeEntry = newAdjustedNetBalance - parseFloat(rowData.netBalance);

    const newRowData = {
      ...rowData,
      [editedFlag]: true,
      adjustedNetBalance: rowData.newRate === null ? null : newAdjustedNetBalance.toFixed(2),
      rateChangeEntry: rowData.newRate === null ? null : newRateChangeEntry.toFixed(2),
    };

    this.setUpdatedRateChanges(newRowData);
    node.setData(newRowData);
  }

  resetPreviousNewValue({ data, node, colDef }) {
    const previousData = this.previousNewRateValue;

    const newRowData = {
      ...data,
      [colDef.field]: previousData,
    };

    if (previousData) {
      this.setUpdatedRateChanges(newRowData);
    }

    node.setData(newRowData);
  }

  setUpdatedRateChanges(newRowData) {
    this.setState(state => ({
      updatedRateChanges: {
        ...state.updatedRateChanges,
        [newRowData.rateChangeId]: newRowData,
      },
    }));
  }

  clearUpdatedRateChanges() {
    this.setState(() => ({
      updatedRateChanges: {},
    }));
  }

  openPromptNewRateDialog = () => {
    this.setState({ promptBulkNewRateDialog: true });
  };

  closePromptNewRateDialog = () => {
    this.setState({ promptBulkNewRateDialog: false });
  };

  batchSetNewRate = value => {
    this.closePromptNewRateDialog();

    const selectedRowNodes = this.getSelectedRowNodes();
    const validatedRows = selectedRowNodes.filter(node => this.isAllowedToChange(node.data));

    validatedRows.forEach(node => {
      this.setNewRate({
        node,
        data: {
          ...node.data,
          newRate: Number(value) / 100,
        },
      });
    });
    if (validatedRows.length !== selectedRowNodes.length) {
      this.props.showAlert('You cannot edit some of the row because you do not have permission to edit some of the company codes');
    }
    this.refreshCells();
  };

  revertChanges = () => {
    Object.entries(this.state.updatedRateChanges).forEach(([rowId, rowData]) => {
      this.setNewRate({
        node: this.gridApi.getRowNode(rowId),
        data: {
          ...rowData,
          newRate: null,
          [editedFlag]: false,
        },
      });
    });

    this.clearUpdatedRateChanges();
  };

  handleTextInputBlur = params => {
    const field = params.colDef.field;
    if (field === 'comment') {
      return;
    }
    this.handleNewRateBlur(params);
  };

  handleEnterKeyPress = params => {
    this.preventSettingPreviousValue = true;
    params.api.stopEditing();
  };

  isAllowedToChange(taxEntry) {
    const { user } = this.props;

    const assignmentsToCheck = {
      assignmentType: assignmentTypes.CC,
      requiredPermission: assignmentPermissions.SubledgerAdd,
    };

    return Boolean(checkAccessBasedOnAssignmentPermissions({
      user,
      companyCode: taxEntry.companyCode,
      withAssignments: [assignmentsToCheck],
    }));
  }

  onGridReady = params => {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  };

  render() {
    const { rateChangeRows, isLoading, isSubmittingRows } = this.props;
    const { promptBulkNewRateDialog } = this.state;

    const extendedColumnDefs = [
      {
        headerName: '',
        field: 'rateChangeId',
        cellRenderer: CheckboxCellRenderer(),
        cellRendererParams: {
          isChecked: value => this.state.rateChangesForReview.includes(value),
          onSelect: this.handleCheckboxSelectionChanged,
        },
        width: 50,
        minWidth: 50,
      },
      ...rateChangeWorkpaperColumnDefs(this.handleEnterKeyPress),
    ];

    return (
      <div>
        <NavigationPrompt show={Object.keys(this.state.updatedRateChanges).length !== 0}/>
        <PromptDialog
          onCancel={this.closePromptNewRateDialog}
          onConfirm={this.batchSetNewRate}
          submitText="Set new rate"
          title="Type new rate value"
          inputType="number"
          isVisible={promptBulkNewRateDialog}
        />
        <div className="row no-gutters">
          <div className="col">
            <div className="page-content-container">
              <div>
                <AgGrid
                  columnDefs={extendedColumnDefs}
                  headerHeight={50}
                  rowData={rateChangeRows}
                  isGridLoading={isLoading || isSubmittingRows}
                  withSearchBar
                  singleClickEdit
                  stopEditingWhenCellsLoseFocus
                  onGridReady={this.onGridReady}
                  onCellEditingStarted={this.handleTextInputFocus}
                  onCellEditingStopped={this.handleTextInputBlur}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="row no-gutters">
          <div className="col">
            <Button
              type="button"
              kind="secondary"
              onClick={this.selectAllRows}
              disabled={isLoading}
            >
              Select All
            </Button>
            <Button
              type="button"
              kind="secondary"
              className="a-mx-10"
              onClick={this.clearAllRows}
              disabled={isLoading || !this.isAnyRowSelected()}
            >
              Clear All
            </Button>
            <Button
              type="button"
              kind="secondary"
              disabled={!this.isAnyRowSelected()}
              onClick={this.openPromptNewRateDialog}
            >
              New Rate for Selected
            </Button>
            <div className="float-right">
              {
                Object.keys(this.state.updatedRateChanges).length !== 0 && (
                  <>
                    <span className="text-danger">{Object.keys(this.state.updatedRateChanges).length} entries pending submissions</span>
                    <Button
                      id="revert-button"
                      type="button"
                      kind="secondary"
                      className="a-mx-10"
                      onClick={this.revertChanges}
                    >
                      Revert
                    </Button>
                  </>
                )
              }
              <Button
                id="submit-button"
                type="button"
                kind="primary"
                onClick={this.submitSelectedCells}
                disabled={isLoading || !this.isAnyRowSelected()}
              >
                Submit Selected
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default RateChangeWorkpaperGrid;
